import { z } from 'zod';
import { BudgetSettings } from './me';
import { ScoringParams, ScoringWeights } from './scoring-configs';

/**
 * Réglages (`data-model.md` § `settings`, `api.md` § Configuration et gouvernance). Les clés
 * stockées sont en `snake_case` ; l'API expose du `camelCase`. Aucune clé d'API n'est renvoyée :
 * `providers.<x>.configured` seulement.
 */

export const BUDGET_CAP_MAX_USD = 10_000;
export const NOTIFICATION_RECIPIENTS_MAX = 20;

const Email = z.string().trim().toLowerCase().pipe(z.email('validation.email'));

/** `PUT /settings/budget` (admin). `warnRatio` en fraction (0,8 = 80 %). */
export const UpdateBudgetBody = z.object({
  monthlyCapUsd: z
    .number('validation.number')
    .positive('validation.positive')
    .max(BUDGET_CAP_MAX_USD, 'validation.max'),
  scanCapUsd: z
    .number('validation.number')
    .positive('validation.positive')
    .max(BUDGET_CAP_MAX_USD, 'validation.max'),
  warnRatio: z.number('validation.number').min(0.1, 'validation.min').max(1, 'validation.max'),
});
export type UpdateBudgetBody = z.infer<typeof UpdateBudgetBody>;

export const NotificationSettings = z.object({
  onScanCompleted: z.boolean(),
  onBudgetWarning: z.boolean(),
  recipients: z.array(Email).max(NOTIFICATION_RECIPIENTS_MAX, 'validation.max'),
});
export type NotificationSettings = z.infer<typeof NotificationSettings>;

/** `PUT /settings/notifications` (admin). */
export const UpdateNotificationsBody = NotificationSettings;
export type UpdateNotificationsBody = z.infer<typeof UpdateNotificationsBody>;

export const PROVIDER_KEYS = ['google_ads', 'dataforseo', 'openai', 'resend'] as const;
export const ProviderKey = z.enum(PROVIDER_KEYS);
export type ProviderKey = z.infer<typeof ProviderKey>;

export const ProviderStatus = z.object({ configured: z.boolean() });
export type ProviderStatus = z.infer<typeof ProviderStatus>;

/** Source active des mots-clés (`KEYWORD_PROVIDER`, ADR-010). */
export const KEYWORD_PROVIDER_KEYS = ['google_ads', 'dataforseo'] as const;
export const KeywordProviderKey = z.enum(KEYWORD_PROVIDER_KEYS);
export type KeywordProviderKey = z.infer<typeof KeywordProviderKey>;

/** Configuration de scoring active, en lecture (aperçu d'override côté client). */
export const ActiveScoring = z.object({
  version: z.number().int(),
  weights: ScoringWeights,
  params: ScoringParams,
});
export type ActiveScoring = z.infer<typeof ActiveScoring>;

/** `GET /settings`. */
export const SettingsResponse = z.object({
  budget: BudgetSettings,
  notifications: NotificationSettings,
  scoring: ActiveScoring,
  providers: z.object({
    google_ads: ProviderStatus,
    dataforseo: ProviderStatus,
    openai: ProviderStatus,
    resend: ProviderStatus,
  }),
  /** Fournisseur de mots-clés actif ; `google_ads` n'est utilisé que s'il est sélectionné. */
  keywordProvider: KeywordProviderKey,
});
export type SettingsResponse = z.infer<typeof SettingsResponse>;
